import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Cookie } from "lucide-react";

const KEY = "fl_cookie_consent";

const T = {
  en: { text: "We use strictly necessary cookies to run the site, and analytics cookies only if you agree — see our",
        cookie: "Cookie Policy", accept: "Accept all", reject: "Necessary only" },
  fr: { text: "Nous utilisons des cookies strictement nécessaires au fonctionnement du site, et des cookies de mesure d'audience uniquement avec votre accord — voir notre",
        cookie: "Politique de cookies", accept: "Tout accepter", reject: "Nécessaires uniquement" },
  es: { text: "Usamos cookies estrictamente necesarias para el sitio, y cookies analíticas solo si lo acepta — consulte nuestra",
        cookie: "Política de cookies", accept: "Aceptar todo", reject: "Solo necesarias" },
  ar: { text: "نستخدم ملفات ارتباط ضرورية لتشغيل الموقع، وملفات تحليلات فقط بموافقتك — راجع",
        cookie: "سياسة ملفات الارتباط", accept: "قبول الكل", reject: "الضرورية فقط" },
  it: { text: "Utilizziamo cookie strettamente necessari per il funzionamento del sito e cookie analitici solo con il suo consenso — consulti la nostra",
        cookie: "Informativa sui cookie", accept: "Accetta tutti", reject: "Solo necessari" },
  nl: { text: "We gebruiken strikt noodzakelijke cookies om de site te laten werken, en analytische cookies alleen als u akkoord gaat — zie onze",
        cookie: "Cookiebeleid", accept: "Alles accepteren", reject: "Alleen noodzakelijke" },
  de: { text: "Wir verwenden unbedingt erforderliche Cookies für den Betrieb der Website sowie Analyse-Cookies nur mit Ihrer Zustimmung – siehe unsere",
        cookie: "Cookie-Richtlinie", accept: "Alle akzeptieren", reject: "Nur notwendige" },
};

export const applyConsent = (value) => {
  try {
    localStorage.setItem(KEY, value);
    // Google Consent Mode v2 + Meta pixel consent: signal the choice to loaded tags.
    import("./MarketingLoader").then((m) => { m.updateConsent(value); m.updateMetaConsent(value); m.updateTikTokConsent(value); m.updateOpenAiConsent(value); }).catch(() => {});
    if (value === "accepted" && typeof window.__flInitPosthog === "function") window.__flInitPosthog();
    window.dispatchEvent(new CustomEvent("fl-cookie-consent", { detail: value }));
    // If analytics were already loaded this session and the visitor now opts out,
    // reload so every tracker (PostHog, GA, Meta, TikTok, Snap, LinkedIn) fully stops.
    if (value === "rejected") {
      // Google tags honour the consent update in place; only the pixels without a
      // consent API need a reload to stop capturing.
      const loaded = window.__flPosthogInited || window.fbq || window.ttq || window.snaptr || window._linkedin_data_partner_ids;
      if (loaded) {
        try { window.posthog?.opt_out_capturing?.(); } catch (_) {}
        window.location.reload();
      }
    }
  } catch (_) {}
};

export const openCookiePreferences = () => {
  try { window.dispatchEvent(new CustomEvent("fl-cookie-open")); } catch (_) {}
};

export default function CookieBanner() {
  const { locale = "en" } = useParams();
  const t = T[locale] || T.en;
  const [choice, setChoice] = useState("pending");

  useEffect(() => {
    try { setChoice(localStorage.getItem(KEY) || "pending"); } catch (_) { setChoice("pending"); }
    const reopen = () => setChoice("pending");
    window.addEventListener("fl-cookie-open", reopen);
    return () => window.removeEventListener("fl-cookie-open", reopen);
  }, []);

  if (choice !== "pending") return null;

  const decide = (value) => { applyConsent(value); setChoice(value); };

  return (
    <div className="fixed bottom-0 inset-x-0 z-[110] p-3 sm:p-4" data-testid="cookie-banner" dir={locale === "ar" ? "rtl" : "ltr"}>
      <div className="max-w-content mx-auto bg-ink text-plaster border border-brass/40 shadow-xl rounded-sm px-4 py-4 sm:px-6 flex flex-col md:flex-row md:items-center gap-4">
        <Cookie size={20} className="text-brass-light shrink-0" />
        <p className="text-xs sm:text-sm text-plaster/85 flex-1 leading-relaxed">
          {t.text}{" "}
          <Link to={`/${locale}/cookies`} className="text-brass-light underline hover:text-brass" data-testid="cookie-banner-policy-link">
            {t.cookie}
          </Link>.
        </p>
        <div className="flex gap-3 shrink-0">
          <button data-testid="cookie-reject" onClick={() => decide("rejected")}
                  className="text-xs sm:text-sm px-4 py-2 border border-plaster/30 text-plaster/85 hover:bg-plaster/10 transition-colors rounded-sm">
            {t.reject}
          </button>
          <button data-testid="cookie-accept" onClick={() => decide("accepted")}
                  className="text-xs sm:text-sm px-5 py-2 bg-brass text-ink font-medium hover:bg-brass-light transition-colors rounded-sm">
            {t.accept}
          </button>
        </div>
      </div>
    </div>
  );
}
